import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ArtworkCard from '../components/ArtworkCard';
import { API } from '../config/api';

export default function Favorites() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFavorites = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/my/favorites`);
      setArtworks(response.data.artworks);
    } catch (error) {
      toast.error('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchFavorites();
  }, [authLoading, isAuthenticated, navigate, fetchFavorites]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Collection</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-[#F5F5F0]">
            My Favorites
          </h1>
          <p className="mt-2 text-[#71717A]">
            {artworks.length} {artworks.length === 1 ? 'piece' : 'pieces'} saved
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={`skeleton-${i}`} className="aspect-[4/5] skeleton" />
            ))}
          </div>
        ) : artworks.length === 0 ? (
          <div className="text-center py-24" data-testid="favorites-empty">
            <Heart className="mx-auto text-[#71717A] mb-6" size={48} />
            <p className="text-[#71717A] text-lg">No favorites yet</p>
            <p className="text-[#71717A] mt-2">
              Tap the heart on any artwork to save it here
            </p>
            <Link to="/gallery" className="btn-primary inline-block mt-6">
              Browse Gallery
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="favorites-grid">
            {artworks.map(artwork => (
              <ArtworkCard key={artwork.id} artwork={artwork} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
